import React, { useMemo, useState } from "react";
import Slider from "rc-slider";

const sortByArea = (a, b) => {
  const areaDiff =
    (a.long_mm || 0) * (a.short_mm || 0) - (b.long_mm || 0) * (b.short_mm || 0);
  if (areaDiff !== 0) return areaDiff;
  return (a.long_mm || 0) - (b.long_mm || 0);
};

const MAT_BORDER_MODE = "mat_border";
const PAPER_MARGIN_MODE = "paper_margin";
const PAPER_MARGIN_MIN_MM = 10;
const PAPER_MARGIN_MAX_MM = 100;
const PAPER_MARGIN_STEP_MM = 5;
const PAPER_MARGIN_DEFAULT_MM = 30;

const clampPaperMarginMm = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return PAPER_MARGIN_DEFAULT_MM;
  }

  const steppedValue =
    Math.round(numericValue / PAPER_MARGIN_STEP_MM) * PAPER_MARGIN_STEP_MM;

  return Math.min(
    PAPER_MARGIN_MAX_MM,
    Math.max(PAPER_MARGIN_MIN_MM, steppedValue),
  );
};

const matStyleOptionLabel = (matStyle) =>
  matStyle?.border === "no_border" ? "None (0mm)" : matStyle?.title;

const OTHER_COLLECTION_ID = "__other__";

/**
 * Sidebar configurator for the Pro Mockup Generator.
 * Renders interactive size, mat and frame-style controls.
 *
 * If `onSubmit` is provided, the form posts via the callback (no page
 * reload). Otherwise it falls back to a GET form submit so the server can
 * re-render the page with mockups.
 */
const MockupConfigurator = ({
  formAction,
  frameSkuSizes = [],
  matStyles = [],
  frameStyleColours = [],
  frameStyleCollections = [],
  initialValues = {},
  source = {},
  onSubmit,
  isSubmitting = false,
  submitLabel,
}) => {
  const [sizeMode, setSizeMode] = useState(
    initialValues.widthMm && initialValues.heightMm ? "custom" : "standard",
  );
  const [frameSkuSizeId, setFrameSkuSizeId] = useState(
    initialValues.frameSkuSizeId ? String(initialValues.frameSkuSizeId) : "",
  );
  const [widthMm, setWidthMm] = useState(initialValues.widthMm || "");
  const [heightMm, setHeightMm] = useState(initialValues.heightMm || "");
  const [matStyleId, setMatStyleId] = useState(
    initialValues.matStyleId ? String(initialValues.matStyleId) : "",
  );
  const [borderMode, setBorderMode] = useState(() =>
    Number(initialValues.borderMm) > 0 ? PAPER_MARGIN_MODE : MAT_BORDER_MODE,
  );
  const [paperMarginMm, setPaperMarginMm] = useState(() =>
    clampPaperMarginMm(initialValues.borderMm),
  );
  const [frameStyleColourId, setFrameStyleColourId] = useState(
    initialValues.frameStyleColourId
      ? String(initialValues.frameStyleColourId)
      : "",
  );

  const selectedFrameStyle = useMemo(
    () =>
      frameStyleColours.find(
        (fsc) => String(fsc.id) === String(frameStyleColourId),
      ),
    [frameStyleColours, frameStyleColourId],
  );

  const hasUncategorisedFrameStyles = useMemo(
    () => frameStyleColours.some((fsc) => !fsc.frame_style_collection_id),
    [frameStyleColours],
  );

  const collectionChips = useMemo(() => {
    const chips = frameStyleCollections.map((c) => ({
      id: String(c.id),
      title: c.title,
    }));
    if (hasUncategorisedFrameStyles) {
      chips.push({ id: OTHER_COLLECTION_ID, title: "Unframed" });
    }
    return chips;
  }, [frameStyleCollections, hasUncategorisedFrameStyles]);

  const [collectionFilterId, setCollectionFilterId] = useState(() => {
    if (selectedFrameStyle) {
      return selectedFrameStyle.frame_style_collection_id
        ? String(selectedFrameStyle.frame_style_collection_id)
        : OTHER_COLLECTION_ID;
    }
    if (frameStyleCollections.length > 0) {
      return String(frameStyleCollections[0].id);
    }
    if (hasUncategorisedFrameStyles) {
      return OTHER_COLLECTION_ID;
    }
    return "";
  });

  const visibleFrameStyles = useMemo(() => {
    if (!collectionFilterId) return frameStyleColours;
    if (collectionFilterId === OTHER_COLLECTION_ID) {
      return frameStyleColours.filter((fsc) => !fsc.frame_style_collection_id);
    }
    return frameStyleColours.filter(
      (fsc) =>
        String(fsc.frame_style_collection_id) === String(collectionFilterId),
    );
  }, [frameStyleColours, collectionFilterId]);

  const handleCollectionFilterChange = (nextCollectionId) => {
    const id = String(nextCollectionId);
    if (id === String(collectionFilterId)) return;
    setCollectionFilterId(id);

    const nextVisible =
      id === OTHER_COLLECTION_ID
        ? frameStyleColours.filter((fsc) => !fsc.frame_style_collection_id)
        : frameStyleColours.filter(
            (fsc) => String(fsc.frame_style_collection_id) === id,
          );

    const firstFrameStyle = nextVisible[0];
    if (firstFrameStyle) {
      setFrameStyleColourId(String(firstFrameStyle.id));
    }
  };

  const matBorderStyles = useMemo(
    () =>
      matStyles.filter(
        (m) => m.border === "mat_border" || m.border === "no_border",
      ),
    [matStyles],
  );
  const selectedMatStyle = useMemo(
    () => matBorderStyles.find((m) => String(m.id) === String(matStyleId)),
    [matBorderStyles, matStyleId],
  );

  const selectedSize = useMemo(
    () => frameSkuSizes.find((s) => String(s.id) === String(frameSkuSizeId)),
    [frameSkuSizes, frameSkuSizeId],
  );

  const isoSizes = useMemo(
    () => [...frameSkuSizes.filter((s) => s.is_a_size)].sort(sortByArea),
    [frameSkuSizes],
  );
  const inchSizes = useMemo(
    () => [...frameSkuSizes.filter((s) => !s.is_a_size)].sort(sortByArea),
    [frameSkuSizes],
  );

  const selectedIsInch =
    selectedSize != null && selectedSize.is_a_size === false;
  const [showInchSizes, setShowInchSizes] = useState(selectedIsInch);
  const usingPaperMargin = borderMode === PAPER_MARGIN_MODE;

  const canSubmit =
    frameStyleColourId &&
    (usingPaperMargin || matStyleId) &&
    ((sizeMode === "standard" && frameSkuSizeId) ||
      (sizeMode === "custom" && widthMm && heightMm));

  const handleFormSubmit = (event) => {
    if (!onSubmit) return;
    event.preventDefault();
    if (!canSubmit || isSubmitting) return;

    const values = {
      frame_sku_size_id:
        sizeMode === "standard" && frameSkuSizeId ? frameSkuSizeId : "",
      width_mm: sizeMode === "custom" ? widthMm : "",
      height_mm: sizeMode === "custom" ? heightMm : "",
      mat_style_id: usingPaperMargin ? "" : matStyleId,
      border_mm: usingPaperMargin ? String(paperMarginMm) : "",
      frame_style_colour_id: frameStyleColourId,
    };
    onSubmit(values);
  };

  return (
    <form
      action={formAction}
      method="get"
      onSubmit={handleFormSubmit}
      className="flex flex-col gap-7 lg:sticky lg:top-6"
    >
      {/* Print size */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-stone-900">
            Print size
          </span>
          <button
            type="button"
            onClick={() => {
              if (sizeMode === "standard") {
                setSizeMode("custom");
                setFrameSkuSizeId("");
              } else {
                setSizeMode("standard");
                setWidthMm("");
                setHeightMm("");
              }
            }}
            className="text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2 bg-transparent border-0 p-0"
          >
            {sizeMode === "standard" ? "Use custom size" : "Standard sizes"}
          </button>
        </div>

        {sizeMode === "standard" ? (
          <div className="flex flex-col gap-3">
            {isoSizes.length > 0 && (
              <SizeGroup
                label="Standard"
                sizes={isoSizes}
                selectedId={frameSkuSizeId}
                onSelect={setFrameSkuSizeId}
              />
            )}

            {inchSizes.length > 0 && showInchSizes && (
              <SizeGroup
                label="Others"
                sizes={inchSizes}
                selectedId={frameSkuSizeId}
                onSelect={setFrameSkuSizeId}
              />
            )}

            {inchSizes.length > 0 && (
              <button
                type="button"
                onClick={() => setShowInchSizes((v) => !v)}
                className="self-start text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2 bg-transparent border-0 p-0"
              >
                {showInchSizes ? "Hide other sizes" : "Show other sizes"}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-stone-500 uppercase tracking-wider">
                  Width
                </span>
                <div className="relative">
                  <input
                    type="number"
                    name="search[width_mm]"
                    min={50}
                    max={1200}
                    value={widthMm}
                    onChange={(e) => setWidthMm(e.target.value)}
                    placeholder="mm"
                    className="form-control text-sm border-stone-200 rounded w-full pr-8"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-stone-400 font-mono pointer-events-none">
                    mm
                  </span>
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-stone-500 uppercase tracking-wider">
                  Height
                </span>
                <div className="relative">
                  <input
                    type="number"
                    name="search[height_mm]"
                    min={50}
                    max={1200}
                    value={heightMm}
                    onChange={(e) => setHeightMm(e.target.value)}
                    placeholder="mm"
                    className="form-control text-sm border-stone-200 rounded w-full pr-8"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-stone-400 font-mono pointer-events-none">
                    mm
                  </span>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Frame style */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-stone-900">
            Frame style
          </span>
          {selectedFrameStyle && (
            <span className="text-xs text-stone-500 truncate ml-2 max-w-[200px]">
              {selectedFrameStyle.title}
              {selectedFrameStyle.is_public === false ? " (Disabled)" : ""}
            </span>
          )}
        </div>

        {collectionChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {collectionChips.map((c) => (
              <CollectionChip
                key={c.id}
                label={c.title}
                active={String(collectionFilterId) === String(c.id)}
                onClick={() => handleCollectionFilterChange(c.id)}
              />
            ))}
          </div>
        )}

        <div className="grid grid-cols-6 gap-1.5">
          {visibleFrameStyles.map((fsc) => {
            const checked = String(frameStyleColourId) === String(fsc.id);
            return (
              <label
                key={fsc.id}
                title={fsc.title}
                className={`relative aspect-square rounded overflow-hidden cursor-pointer bg-stone-100 transition-all ${
                  checked
                    ? "ring-2 ring-stone-900 ring-offset-2 ring-offset-stone-50"
                    : "border border-stone-200 hover:border-stone-400"
                }`}
              >
                <input
                  type="radio"
                  name="search[frame_style_colour_id]"
                  value={fsc.id}
                  checked={checked}
                  onChange={() => setFrameStyleColourId(String(fsc.id))}
                  className="sr-only"
                />
                {fsc.profile_thumb ? (
                  <img
                    src={fsc.profile_thumb}
                    alt={fsc.title}
                    className="w-full h-full object-cover block"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-full h-full"
                    style={{ background: fsc.swatch_color || "#a8a29e" }}
                  />
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Border treatment */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-stone-900">
            Border
          </span>
          {usingPaperMargin ? (
            <span className="text-xs text-stone-500 truncate ml-2">
              {paperMarginMm}mm paper margin
            </span>
          ) : (
            selectedMatStyle && (
              <span className="text-xs text-stone-500 truncate ml-2">
                {matStyleOptionLabel(selectedMatStyle)}
              </span>
            )
          )}
        </div>

        <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-stone-200 bg-stone-100 p-1">
          <BorderModeButton
            active={!usingPaperMargin}
            label="Mat Border"
            onClick={() => setBorderMode(MAT_BORDER_MODE)}
          />
          <BorderModeButton
            active={usingPaperMargin}
            label="Paper Margin"
            onClick={() => setBorderMode(PAPER_MARGIN_MODE)}
          />
        </div>

        {usingPaperMargin ? (
          <div className="rounded-lg border border-stone-200 bg-white px-4 py-4">
            <input
              type="hidden"
              name="search[border_mm]"
              value={paperMarginMm}
            />

            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-stone-900">
                Inner paper margin
              </span>
              <span className="text-sm text-stone-500">{paperMarginMm}mm</span>
            </div>

            <p className="mt-2 mb-0 text-[12px] leading-relaxed text-stone-500">
              Applied inside the print area. Note this method effects the crop
              of the image.
            </p>

            <div className="mt-4 px-1">
              <Slider
                min={PAPER_MARGIN_MIN_MM}
                max={PAPER_MARGIN_MAX_MM}
                step={PAPER_MARGIN_STEP_MM}
                value={paperMarginMm}
                onChange={(value) =>
                  setPaperMarginMm(
                    clampPaperMarginMm(Array.isArray(value) ? value[0] : value),
                  )
                }
              />
            </div>

            <div className="mt-2 flex items-center justify-between text-[10px] font-mono tracking-[0.08em] uppercase text-stone-400">
              <span>{PAPER_MARGIN_MIN_MM}mm</span>
              <span>{PAPER_MARGIN_MAX_MM}mm</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {matBorderStyles.map((m) => {
              const checked = String(matStyleId) === String(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex-1 min-w-[60px] flex items-center justify-center px-2.5 py-2 rounded border cursor-pointer transition-colors text-center ${
                    checked
                      ? "border-stone-900 border-[1.5px] bg-white text-stone-900"
                      : "border-stone-200 bg-white text-stone-600 hover:border-stone-400"
                  }`}
                >
                  <input
                    type="radio"
                    name="search[mat_style_id]"
                    value={m.id}
                    checked={checked}
                    onChange={() => setMatStyleId(String(m.id))}
                    className="sr-only"
                  />
                  <span className="text-[12px] font-medium">
                    {matStyleOptionLabel(m)}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Generate CTA */}
      <button
        type="submit"
        disabled={!canSubmit || isSubmitting}
        className="btn btn-primary w-full mt-1 text-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? "Generating…" : submitLabel || "Generate mockups"}
      </button>

      {/* Outside dimensions readout (server-rendered after generate) */}
      {source.outsideDimensions && (
        <div className="px-4 py-3 bg-stone-100 rounded flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] font-mono tracking-widest text-stone-500 uppercase">
              Outside dimensions
            </span>
            <span className="text-sm font-medium text-stone-900 mt-0.5">
              {source.outsideDimensions}
            </span>
          </div>
          <span className="text-[11px] text-stone-500">
            finished outer size
          </span>
        </div>
      )}

      {/* Notes */}
      <div className="flex flex-col gap-2 pt-5 border-t border-stone-200">
        <span className="text-[10px] font-mono tracking-widest text-stone-400 uppercase">
          Notes
        </span>
        <p className="text-[13px] leading-snug text-stone-600 m-0">
          Click the download icon on any mockup to save it. Sizes shown are
          common configurations &mdash; not every combination is in stock.
        </p>
      </div>
    </form>
  );
};

const CollectionChip = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full border text-[11px] font-medium tracking-wide transition-colors ${
      active
        ? "bg-stone-900 border-stone-900 text-white"
        : "bg-white border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900"
    }`}
  >
    {label}
  </button>
);

const BorderModeButton = ({ active, label, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`rounded-md px-3 py-2 text-[12px] font-medium transition-colors ${
      active
        ? "bg-white text-stone-900 shadow-sm"
        : "text-stone-500 hover:text-stone-900"
    }`}
  >
    {label}
  </button>
);

const SizeGroup = ({ label, sizes, selectedId, onSelect }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-stone-400">
      {label}
    </span>
    <div className="grid grid-cols-4 gap-1.5">
      {sizes.map((s) => {
        const checked = String(selectedId) === String(s.id);
        return (
          <label
            key={s.id}
            title={s.dimensions ? `${s.title} \u2014 ${s.dimensions}` : s.title}
            className={`relative flex items-center justify-center px-1.5 py-2 rounded border cursor-pointer transition-colors text-center ${
              checked
                ? "border-stone-900 border-[1.5px] bg-white"
                : "border-stone-200 bg-white hover:border-stone-400"
            }`}
          >
            <input
              type="radio"
              name="search[frame_sku_size_id]"
              value={s.id}
              checked={checked}
              onChange={() => onSelect(String(s.id))}
              className="sr-only"
            />
            <span className="text-[12px] font-medium text-stone-900 leading-tight truncate">
              {s.title}
            </span>
          </label>
        );
      })}
    </div>
  </div>
);

export default MockupConfigurator;
